import React, { useId, useRef } from "react";
import PropTypes from "prop-types";
import styled, { css, keyframes } from "styled-components";
import themeGet from "@styled-system/theme-get";
import { Box, Icon } from "orcs-design-system";
import {
  ASK_TEAMFORM_MODES,
  ASK_TEAMFORM_MODE_UI,
  AI_BRAND_GRADIENT,
} from "./consts";

const slideHint = keyframes`
  from { opacity: 0; transform: translateY(3px); }
  to { opacity: 1; transform: translateY(0); }
`;

const SwitcherRoot = styled(Box)`
  padding: 10px 12px 0;
`;

const ModeBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
`;

const ModeLabel = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${themeGet("colors.greyDarker")};
  opacity: 0.7;
  white-space: nowrap;
`;

const SegmentTrack = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: 1fr 1fr;
  flex: 1;
  max-width: 248px;
  padding: 3px;
  border-radius: 999px;
  background: ${themeGet("colors.surfaceRaised")};
  box-shadow: inset 0 1px 2px rgba(15, 23, 42, 0.04);
`;

const ActiveIndicator = styled.div`
  position: absolute;
  top: 3px;
  bottom: 3px;
  left: 3px;
  width: calc(50% - 3px);
  border-radius: 999px;
  background: ${themeGet("colors.surface")};
  box-shadow:
    0 1px 3px rgba(15, 23, 42, 0.06),
    0 4px 12px rgba(0, 145, 234, 0.1);
  transition: transform 0.28s cubic-bezier(0.34, 1.2, 0.64, 1);
  transform: translateX(${({ $index }) => ($index === 0 ? "0" : "100%")});
  z-index: 0;
`;

const SegmentButton = styled.button`
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  min-height: 28px;
  padding: 4px 10px;
  border: none;
  border-radius: 999px;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  background: transparent;
  transition: color 0.18s ease;
  color: ${({ $active, theme }) =>
    $active ? theme.colors.bodyText : theme.colors.greyDarker};

  ${({ $active }) =>
    !$active &&
    css`
      &:hover:not(:disabled) {
        color: ${themeGet("colors.bodyText")};
      }
    `}

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }

  &:focus-visible {
    outline: 2px solid rgba(0, 145, 234, 0.3);
    outline-offset: 1px;
  }
`;

const IconDot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  font-size: 10px;
  transition: all 0.22s ease;

  ${({ $active }) =>
    $active
      ? css`
          background: ${AI_BRAND_GRADIENT};
          color: #fff;
          box-shadow: 0 1px 4px rgba(0, 145, 234, 0.25);
        `
      : css`
          background: ${themeGet("colors.surfaceBorder")};
          color: ${themeGet("colors.greyDarker")};
        `}
`;

const HintText = styled.p`
  margin: 0 2px 10px;
  font-size: 11px;
  line-height: 1.35;
  color: ${themeGet("colors.greyDarker")};
  animation: ${slideHint} 0.2s ease;
`;

const MODES = [ASK_TEAMFORM_MODES.DATA, ASK_TEAMFORM_MODES.SUPPORT];

const AskTeamformModeToggle = ({ mode, onModeChange, disabled }) => {
  const groupId = useId();
  const tabRefs = useRef({});
  const resolvedMode =
    mode === ASK_TEAMFORM_MODES.SUPPORT
      ? ASK_TEAMFORM_MODES.SUPPORT
      : ASK_TEAMFORM_MODES.DATA;
  const activeIndex = resolvedMode === ASK_TEAMFORM_MODES.SUPPORT ? 1 : 0;
  const activeUi = ASK_TEAMFORM_MODE_UI[resolvedMode];

  const handleTabKeyDown = (event, modeKey) => {
    if (disabled) {
      return;
    }

    const { key } = event;
    if (key !== "ArrowLeft" && key !== "ArrowRight") {
      return;
    }

    event.preventDefault();
    const currentIndex = MODES.indexOf(modeKey);
    const nextIndex =
      key === "ArrowRight"
        ? (currentIndex + 1) % MODES.length
        : (currentIndex - 1 + MODES.length) % MODES.length;
    const nextMode = MODES[nextIndex];
    onModeChange(nextMode);
    // Roving tabIndex: move focus with the selection, or the old tab
    // (now tabIndex=-1) keeps focus and further arrow keys act on it.
    tabRefs.current[nextMode]?.focus();
  };

  return (
    <SwitcherRoot>
      <ModeBar>
        <ModeLabel>Ask about</ModeLabel>
        <SegmentTrack role="tablist" aria-label="Ask TeamForm question type">
          <ActiveIndicator $index={activeIndex} aria-hidden />
          {MODES.map((modeKey) => {
            const ui = ASK_TEAMFORM_MODE_UI[modeKey];
            const isActive = resolvedMode === modeKey;

            return (
              <SegmentButton
                key={modeKey}
                ref={(node) => {
                  tabRefs.current[modeKey] = node;
                }}
                id={`${groupId}-${modeKey}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`${groupId}-hint`}
                tabIndex={isActive ? 0 : -1}
                $active={isActive}
                disabled={disabled}
                onClick={() => onModeChange(modeKey)}
                onKeyDown={(event) => handleTabKeyDown(event, modeKey)}
              >
                <IconDot $active={isActive} aria-hidden>
                  <Icon icon={ui.icon} />
                </IconDot>
                {ui.label}
              </SegmentButton>
            );
          })}
        </SegmentTrack>
      </ModeBar>
      <HintText
        id={`${groupId}-hint`}
        role="tabpanel"
        aria-live="polite"
        aria-labelledby={`${groupId}-${resolvedMode}`}
      >
        {activeUi.hint}
      </HintText>
    </SwitcherRoot>
  );
};

AskTeamformModeToggle.propTypes = {
  mode: PropTypes.oneOf(MODES),
  onModeChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

AskTeamformModeToggle.defaultProps = {
  mode: ASK_TEAMFORM_MODES.DATA,
};

export default AskTeamformModeToggle;
